import {
  Bell,
  BookOpenText,
  Briefcase,
  Chat,
  CircleDashed,
  ClockCounterClockwise,
  Envelope,
  Factory,
  FunnelSimple,
  GraduationCap,
  HandHeart,
  HandsClapping,
  Heart,
  type Icon,
  IdentificationBadge,
  Lightbulb,
  LinkSimple,
  ListStar,
  MapPin,
  RowsPlusBottom,
  TextAlignLeft,
  ThumbsUp,
  Translate,
  Trash,
  UserList,
  UsersThree,
} from "@phosphor-icons/react";

import type { TagVariantType } from "common/components/ui/Tag";
import { CAMPAIGN_STATES, CAMPAIGN_TYPES } from "common/constants";
import { typedObjectEntries } from "common/helpers/utils";
import type {
  CampaignOutreachType,
  CampaignType,
  DetailedWorkspace,
  KeywordModifiers,
  KeywordTypes,
  Metrics,
  MetricsParameters,
  ProfileSearchParams,
  UserPermissions,
} from "common/types";

import type { Icon as IconType } from "@phosphor-icons/react/dist/lib/types";
import type { FunctionComponent, JSX } from "react";
import type {
  AudienceFilters,
  AudienceProfileState,
  CampaignFlowType,
  CampaignMetrics,
  CampaignsSearchParams,
  CancelledTargetState,
  NavigatorFilterMeta,
  ReactionType,
  TimezoneOption,
} from "./types";

import SmileyHappy from "assets/icons/smiley-happy.svg?react";
import ViewProfileFull from "assets/icons/view-profile-full.svg?react";

export const KEYWORD_MODIFIER_OPTIONS: Record<
  KeywordModifiers,
  { tagVariant: TagVariantType; additionalClasses: string }
> = {
  required: {
    tagVariant: "success",
    additionalClasses: "hover:bg-green-100 data-[state=open]:bg-green-100",
  },
  excluded: {
    tagVariant: "danger",
    additionalClasses: "hover:bg-red-100 data-[state=open]:bg-red-100",
  },
  optional: {
    tagVariant: "secondary",
    additionalClasses: "hover:bg-purple-100 data-[state=open]:bg-purple-100",
  },
  raw: {
    tagVariant: "info",
    additionalClasses: "hover:bg-blue-100 data-[state=open]:bg-blue-100",
  },
};

export const KEYWORD_OPTIONS: Record<
  KeywordTypes,
  { label: string; icon: Icon }
> = {
  general: {
    label: "General",
    icon: TextAlignLeft,
  },
  company: {
    label: "Company",
    icon: Briefcase,
  },
  school: {
    label: "School",
    icon: GraduationCap,
  },
  title: {
    label: "Title",
    icon: IdentificationBadge,
  },
};

export const PROFILE_SEARCH_PARAMS_ICONS: Record<
  keyof ProfileSearchParams,
  Icon
> = {
  keywords: TextAlignLeft,
  locations: MapPin,
  industries: Factory,
  current_companies: Briefcase,
  past_companies: Briefcase,
  followers_of: UsersThree,
  schools: GraduationCap,
  languages: Translate,
  skills: BookOpenText,
} as const;

export const PROFILE_SEARCH_OPTIONS: Record<
  keyof Omit<ProfileSearchParams, "keywords">,
  {
    icon: Icon;
    label: string;
    keywordsKey?: KeywordTypes;
  }
> = {
  locations: {
    label: "Locations",
    icon: MapPin,
  },
  industries: {
    label: "Industries",
    icon: Factory,
  },
  current_companies: {
    label: "Current companies",
    icon: Briefcase,
    keywordsKey: "company",
  },
  past_companies: {
    label: "Past companies",
    icon: ClockCounterClockwise,
    keywordsKey: "company",
  },
  followers_of: {
    label: "Followers of",
    icon: UsersThree,
  },
  schools: {
    label: "Schools",
    icon: GraduationCap,
    keywordsKey: "school",
  },
  skills: { label: "Skills", icon: BookOpenText },
  languages: {
    label: "Profile Languages",
    icon: Translate,
  },
};

export const sequenceDelayOptions = (isDesktop?: boolean) => {
  const dayOptions = Array.from({ length: 31 }, (_, i) => ({
    value: i,
    label: `${i < 10 ? "0" : ""}${i}${isDesktop ? "" : ` Day${i === 1 ? "" : "s"}`}`,
  }));

  const hourOptions = Array.from({ length: 25 }, (_, i) => ({
    value: i,
    label: `${i < 10 ? "0" : ""}${i}${isDesktop ? "" : ` Hour${i === 1 ? "" : "s"}`}`,
  }));

  return { dayOptions, hourOptions };
};

export const AUDIENCE_PROFILE_STATES: {
  states: AudienceProfileState[];
  tagVariant: TagVariantType;
  label: string;
}[] = [
  {
    states: ["init", "init_branch"],
    tagVariant: "quaternary",
    label: "Initialized",
  },
  {
    states: ["view", "view_branch"],
    tagVariant: "magenta",
    label: "Viewing profile",
  },
  {
    states: ["connect"],
    tagVariant: "yellow",
    label: "Sending connection",
  },
  {
    states: ["withdraw"],
    tagVariant: "orange",
    label: "Connection sent",
  },
  {
    states: ["inmail"],
    tagVariant: "secondary",
    label: "Sending InMail",
  },
  {
    states: ["message", "message_request"],
    tagVariant: "info",
    label: "Sending message",
  },
  { states: ["like"], tagVariant: "secondary", label: "Reacting to a post" },
  {
    states: ["endorse"],
    tagVariant: "secondary",
    label: "Endorsing a skill",
  },
  {
    states: ["done"],
    tagVariant: "success",
    label: "Completed",
  },
  {
    states: ["cancelled"],
    tagVariant: "danger",
    label: "Cancelled",
  },
] as const;

export const CANCELLED_TARGET_STATES: Record<CancelledTargetState, string> = {
  cant_branch: "Can't branch",
  cant_resend_yet: "Can't resend yet",
  message_too_long: "Message too long",
  blocked: "Blocked",
  already_connected: "Already connected",
  cant_message: "Can't message",
  not_connected: "Not connected",
  missing_variables: "Missing variables",
  not_in_event: "Not in event",
  not_in_group: "Not in group",
  manual_cancel: "Cancelled manually",
};

export const FLOW_TYPE_OPTIONS: Record<
  CampaignFlowType,
  {
    tagVariant: TagVariantType;
    icon: Icon;
    classNames: string;
  }
> = {
  CONNECT_OPTIMIZATION: {
    tagVariant: "orange",
    icon: LinkSimple,
    classNames: "bg-orange-100 text-orange-600",
  },
  INMAIL_OPTIMIZATION: {
    tagVariant: "secondary",
    icon: Envelope,
    classNames: "bg-purple-100 text-purple-600",
  },
  PRIMARY_CONNECT: {
    tagVariant: "orange",
    icon: LinkSimple,
    classNames: "bg-orange-100 text-orange-600",
  },
  PRIMARY_MESSAGE: {
    tagVariant: "info",
    icon: Chat,
    classNames: "bg-blue-100 text-blue-500",
  },
};

export const CAMPAIGN_OUTREACH_OPTIONS: Record<
  CampaignOutreachType,
  {
    icon: Icon;
    hiddenMetricsColumns: (keyof Metrics)[];
    getSuccessString: (metrics: CampaignMetrics) => string;
  }
> = {
  connect: {
    icon: LinkSimple,
    hiddenMetricsColumns: ["message_requests"],
    getSuccessString: ({
      sent_connections,
      sent_inmails,
      accepted_connections,
      inmail_replies,
    }) =>
      `${accepted_connections + inmail_replies}/${sent_connections + sent_inmails}`,
  },
  message: {
    icon: Chat,
    hiddenMetricsColumns: ["sent_connections", "accepted_connections"],
    getSuccessString: ({ replies, message_requests, sent_inmails }) =>
      `${replies}/${message_requests + sent_inmails}`,
  },
  inmail: {
    icon: Envelope,
    hiddenMetricsColumns: [
      "sent_connections",
      "sent_messages",
      "accepted_connections",
      "message_requests",
    ],
    getSuccessString: ({ inmail_replies, sent_inmails }) =>
      `${inmail_replies}/${sent_inmails}`,
  },
  drip: {
    icon: Chat,
    hiddenMetricsColumns: [
      "views",
      "sent_connections",
      "accepted_connections",
      "message_requests",
      "sent_inmails",
    ],
    getSuccessString: ({ replies, sent_messages }) =>
      `${replies}/${sent_messages}`,
  },
};

export const STATS_TIMEFRAME_OPTIONS: {
  id: string;
  label: string;
  getParameters: (workspace: DetailedWorkspace) => MetricsParameters;
}[] = [
  {
    id: "all",
    label: "All time",
    getParameters: (workspace) => ({
      bucketSize: "1 week",
      from: workspace.created_at * 1000,
      to: Date.now(),
    }),
  },
  {
    id: "30d",
    label: "30-day",
    getParameters: () => {
      const dateNow = new Date();
      dateNow.setHours(0, 0, 0, 0);
      return {
        bucketSize: "1 day",
        from: dateNow.getTime() - 29 * 24 * 60 * 60 * 1000,
        to: Date.now(),
      };
    },
  },
  {
    id: "7d",
    label: "7-day",
    getParameters: () => {
      const dateNow = new Date();
      dateNow.setHours(0, 0, 0, 0);
      return {
        bucketSize: "1 day",
        from: dateNow.getTime() - 6 * 24 * 60 * 60 * 1000,
        to: Date.now(),
      };
    },
  },
  {
    id: "24h",
    label: "Last 24h",
    getParameters: () => ({
      bucketSize: "1 hour",
      from: Date.now() - 24 * 60 * 60 * 1000,
      to: Date.now(),
    }),
  },
];

export const DRIP_FACET_KEYS = [
  "locations",
  "current_companies",
  "past_companies",
  "schools",
  "origins",
  "labels",
  "skills",
  "interests",
  "languages",
] as const;

export const AUDIENCE_FACET_KEYS = [
  "languages",
  "current_companies",
  "past_companies",
  "schools",
  "locations",
  "premium",
] as const;

export const DEFAULT_AUDIENCE_FILTERS: AudienceFilters = {
  keyword: "",
  languages: [],
  current_companies: [],
  past_companies: [],
  schools: [],
  locations: [],
  premium: undefined,
} as const;

export const FAVORITE_FILTER_OPTIONS = [
  {
    text: "All",
    value: false,
  },
  {
    text: "Only favorite",
    value: true,
  },
];

export const REACTIONS: Record<
  ReactionType,
  { label: string; icon: IconType | FunctionComponent }
> = {
  LIKE: { label: "Like", icon: ThumbsUp },
  PRAISE: { label: "Celebrate", icon: HandsClapping },
  EMPATHY: { label: "Support", icon: HandHeart },
  APPRECIATION: { label: "Love", icon: Heart },
  INTEREST: { label: "Insightful", icon: Lightbulb },
  ENTERTAINMENT: { label: "Funny", icon: SmileyHappy },
};

export const TIMEZONES: TimezoneOption[] = [
  {
    label: "Target's Timezone (Recommended)",
    value: "default",
    offset: null,
  },
  {
    label: "(UTC-12:00) International Date Line West",
    value: "Dateline Standard Time",
    offset: -12,
  },
  {
    label: "(UTC-11:00) Coordinated Universal Time-11",
    value: "UTC-11",
    offset: -11,
  },
  {
    label: "(UTC-10:00) Hawaii",
    value: "Hawaiian Standard Time",
    offset: -10,
  },
  {
    label: "(UTC-09:00) Alaska",
    value: "Alaskan Standard Time",
    offset: -8,
  },
  {
    label: "(UTC-08:00) Baja California",
    value: "Pacific Standard Time (Mexico)",
    offset: -7,
  },
  {
    label: "(UTC-07:00) Pacific Daylight Time (US & Canada)",
    value: "Pacific Daylight Time",
    offset: -7,
  },
  {
    label: "(UTC-08:00) Pacific Standard Time (US & Canada)",
    value: "Pacific Standard Time",
    offset: -8,
  },
  {
    label: "(UTC-07:00) Arizona",
    value: "US Mountain Standard Time",
    offset: -7,
  },
  {
    label: "(UTC-07:00) Chihuahua, La Paz, Mazatlan",
    value: "Mountain Standard Time (Mexico)",
    offset: -6,
  },
  {
    label: "(UTC-07:00) Mountain Time (US & Canada)",
    value: "Mountain Standard Time",
    offset: -6,
  },
  {
    label: "(UTC-06:00) Central America",
    value: "Central America Standard Time",
    offset: -6,
  },
  {
    label: "(UTC-06:00) Central Time (US & Canada)",
    value: "Central Standard Time",
    offset: -5,
  },
  {
    label: "(UTC-06:00) Guadalajara, Mexico City, Monterrey",
    value: "Central Standard Time (Mexico)",
    offset: -5,
  },
  {
    label: "(UTC-06:00) Saskatchewan",
    value: "Canada Central Standard Time",
    offset: -6,
  },
  {
    label: "(UTC-05:00) Bogota, Lima, Quito",
    value: "SA Pacific Standard Time",
    offset: -5,
  },
  {
    label: "(UTC-05:00) Eastern Time (US & Canada)",
    value: "Eastern Standard Time",
    offset: -5,
  },
  {
    label: "(UTC-04:00) Eastern Daylight Time (US & Canada)",
    value: "Eastern Daylight Time",
    offset: -4,
  },
  {
    label: "(UTC-05:00) Indiana (East)",
    value: "US Eastern Standard Time",
    offset: -5,
  },
  {
    label: "(UTC-04:30) Caracas",
    value: "Venezuela Standard Time",
    offset: -5,
  },
  {
    label: "(UTC-04:00) Asuncion",
    value: "Paraguay Standard Time",
    offset: -4,
  },
  {
    label: "(UTC-04:00) Atlantic Time (Canada)",
    value: "Atlantic Standard Time",
    offset: -3,
  },
  {
    label: "(UTC-04:00) Cuiaba",
    value: "Central Brazilian Standard Time",
    offset: -4,
  },
  {
    label: "(UTC-04:00) Georgetown, La Paz, Manaus, San Juan",
    value: "SA Western Standard Time",
    offset: -4,
  },
  {
    label: "(UTC-04:00) Santiago",
    value: "Pacific SA Standard Time",
    offset: -4,
  },
  {
    label: "(UTC-03:30) Newfoundland",
    value: "Newfoundland Standard Time",
    offset: -3,
  },
  {
    label: "(UTC-03:00) Brasilia",
    value: "E. South America Standard Time",
    offset: -3,
  },
  {
    label: "(UTC-03:00) Buenos Aires",
    value: "Argentina Standard Time",
    offset: -3,
  },
  {
    label: "(UTC-03:00) Cayenne, Fortaleza",
    value: "SA Eastern Standard Time",
    offset: -3,
  },
  {
    label: "(UTC-03:00) Greenland",
    value: "Greenland Standard Time",
    offset: -3,
  },
  {
    label: "(UTC-03:00) Montevideo",
    value: "Montevideo Standard Time",
    offset: -3,
  },
  {
    label: "(UTC-03:00) Salvador",
    value: "Bahia Standard Time",
    offset: -3,
  },
  {
    label: "(UTC-02:00) Coordinated Universal Time-02",
    value: "UTC-02",
    offset: -2,
  },
  {
    label: "(UTC-02:00) Mid-Atlantic - Old",
    value: "Mid-Atlantic Standard Time",
    offset: -1,
  },
  {
    label: "(UTC-01:00) Azores",
    value: "Azores Standard Time",
    offset: 0,
  },
  {
    label: "(UTC-01:00) Cape Verde Is.",
    value: "Cape Verde Standard Time",
    offset: -1,
  },
  {
    label: "(UTC) Casablanca",
    value: "Morocco Standard Time",
    offset: 1,
  },
  {
    label: "(UTC) Coordinated Universal Time",
    value: "UTC",
    offset: 0,
  },
  {
    label: "(UTC) Edinburgh, London",
    value: "GMT Standard Time",
    offset: 0,
  },
  {
    label: "(UTC+01:00) Edinburgh, London",
    value: "British Summer Time",
    offset: 1,
  },
  {
    label: "(UTC) Dublin, Lisbon",
    value: "GMT Standard Time",
    offset: 1,
  },
  {
    label: "(UTC) Monrovia, Reykjavik",
    value: "Greenwich Standard Time",
    offset: 0,
  },
  {
    label: "(UTC+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna",
    value: "W. Europe Standard Time",
    offset: 2,
  },
  {
    label: "(UTC+01:00) Belgrade, Bratislava, Budapest, Ljubljana, Prague",
    value: "Central Europe Standard Time",
    offset: 2,
  },
  {
    label: "(UTC+01:00) Brussels, Copenhagen, Madrid, Paris",
    value: "Romance Standard Time",
    offset: 2,
  },
  {
    label: "(UTC+01:00) Sarajevo, Skopje, Warsaw, Zagreb",
    value: "Central European Standard Time",
    offset: 2,
  },
  {
    label: "(UTC+01:00) West Central Africa",
    value: "W. Central Africa Standard Time",
    offset: 1,
  },
  {
    label: "(UTC+01:00) Windhoek",
    value: "Namibia Standard Time",
    offset: 1,
  },
  {
    label: "(UTC+02:00) Athens, Bucharest",
    value: "GTB Standard Time",
    offset: 3,
  },
  {
    label: "(UTC+02:00) Beirut",
    value: "Middle East Standard Time",
    offset: 3,
  },
  {
    label: "(UTC+02:00) Cairo",
    value: "Egypt Standard Time",
    offset: 2,
  },
  {
    label: "(UTC+02:00) Damascus",
    value: "Syria Standard Time",
    offset: 3,
  },
  {
    label: "(UTC+02:00) E. Europe",
    value: "E. Europe Standard Time",
    offset: 3,
  },
  {
    label: "(UTC+02:00) Harare, Pretoria",
    value: "South Africa Standard Time",
    offset: 2,
  },
  {
    label: "(UTC+02:00) Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius",
    value: "FLE Standard Time",
    offset: 3,
  },
  {
    label: "(UTC+03:00) Istanbul",
    value: "Turkey Standard Time",
    offset: 3,
  },
  {
    label: "(UTC+02:00) Jerusalem",
    value: "Israel Standard Time",
    offset: 3,
  },
  {
    label: "(UTC+02:00) Tripoli",
    value: "Libya Standard Time",
    offset: 2,
  },
  {
    label: "(UTC+03:00) Amman",
    value: "Jordan Standard Time",
    offset: 3,
  },
  {
    label: "(UTC+03:00) Baghdad",
    value: "Arabic Standard Time",
    offset: 3,
  },
  {
    label: "(UTC+02:00) Kaliningrad",
    value: "Kaliningrad Standard Time",
    offset: 3,
  },
  {
    label: "(UTC+03:00) Kuwait, Riyadh",
    value: "Arab Standard Time",
    offset: 3,
  },
  {
    label: "(UTC+03:00) Nairobi",
    value: "E. Africa Standard Time",
    offset: 3,
  },
  {
    label: "(UTC+03:00) Moscow, St. Petersburg, Volgograd, Minsk",
    value: "Moscow Standard Time",
    offset: 3,
  },
  {
    label: "(UTC+04:00) Samara, Ulyanovsk, Saratov",
    value: "Samara Time",
    offset: 4,
  },
  {
    label: "(UTC+03:30) Tehran",
    value: "Iran Standard Time",
    offset: 5,
  },
  {
    label: "(UTC+04:00) Abu Dhabi, Muscat",
    value: "Arabian Standard Time",
    offset: 4,
  },
  {
    label: "(UTC+04:00) Baku",
    value: "Azerbaijan Standard Time",
    offset: 5,
  },
  {
    label: "(UTC+04:00) Port Louis",
    value: "Mauritius Standard Time",
    offset: 4,
  },
  {
    label: "(UTC+04:00) Tbilisi",
    value: "Georgian Standard Time",
    offset: 4,
  },
  {
    label: "(UTC+04:00) Yerevan",
    value: "Caucasus Standard Time",
    offset: 4,
  },
  {
    label: "(UTC+04:30) Kabul",
    value: "Afghanistan Standard Time",
    offset: 5,
  },
  {
    label: "(UTC+05:00) Ashgabat, Tashkent",
    value: "West Asia Standard Time",
    offset: 5,
  },
  {
    label: "(UTC+05:00) Yekaterinburg",
    value: "Yekaterinburg Time",
    offset: 5,
  },
  {
    label: "(UTC+05:00) Islamabad, Karachi",
    value: "Pakistan Standard Time",
    offset: 5,
  },
  {
    label: "(UTC+05:30) Chennai, Kolkata, Mumbai, New Delhi",
    value: "India Standard Time",
    offset: 6,
  },
  {
    label: "(UTC+05:30) Sri Jayawardenepura",
    value: "Sri Lanka Standard Time",
    offset: 6,
  },
  {
    label: "(UTC+05:45) Kathmandu",
    value: "Nepal Standard Time",
    offset: 6,
  },
  {
    label: "(UTC+06:00) Nur-Sultan (Astana)",
    value: "Central Asia Standard Time",
    offset: 6,
  },
  {
    label: "(UTC+06:00) Dhaka",
    value: "Bangladesh Standard Time",
    offset: 6,
  },
  {
    label: "(UTC+06:30) Yangon (Rangoon)",
    value: "Myanmar Standard Time",
    offset: 7,
  },
  {
    label: "(UTC+07:00) Bangkok, Hanoi, Jakarta",
    value: "SE Asia Standard Time",
    offset: 7,
  },
  {
    label: "(UTC+07:00) Novosibirsk",
    value: "N. Central Asia Standard Time",
    offset: 7,
  },
  {
    label: "(UTC+08:00) Beijing, Chongqing, Hong Kong, Urumqi",
    value: "China Standard Time",
    offset: 8,
  },
  {
    label: "(UTC+08:00) Krasnoyarsk",
    value: "North Asia Standard Time",
    offset: 8,
  },
  {
    label: "(UTC+08:00) Kuala Lumpur, Singapore",
    value: "Singapore Standard Time",
    offset: 8,
  },
  {
    label: "(UTC+08:00) Perth",
    value: "W. Australia Standard Time",
    offset: 8,
  },
  {
    label: "(UTC+08:00) Taipei",
    value: "Taipei Standard Time",
    offset: 8,
  },
  {
    label: "(UTC+08:00) Ulaanbaatar",
    value: "Ulaanbaatar Standard Time",
    offset: 8,
  },
  {
    label: "(UTC+08:00) Irkutsk",
    value: "North Asia East Standard Time",
    offset: 8,
  },
  {
    label: "(UTC+09:00) Osaka, Sapporo, Tokyo",
    value: "Japan Standard Time",
    offset: 9,
  },
  {
    label: "(UTC+09:00) Seoul",
    value: "Korea Standard Time",
    offset: 9,
  },
  {
    label: "(UTC+09:30) Adelaide",
    value: "Cen. Australia Standard Time",
    offset: 10,
  },
  {
    label: "(UTC+09:30) Darwin",
    value: "AUS Central Standard Time",
    offset: 10,
  },
  {
    label: "(UTC+10:00) Brisbane",
    value: "E. Australia Standard Time",
    offset: 10,
  },
  {
    label: "(UTC+10:00) Canberra, Melbourne, Sydney",
    value: "AUS Eastern Standard Time",
    offset: 10,
  },
  {
    label: "(UTC+10:00) Guam, Port Moresby",
    value: "West Pacific Standard Time",
    offset: 10,
  },
  {
    label: "(UTC+10:00) Hobart",
    value: "Tasmania Standard Time",
    offset: 10,
  },
  {
    label: "(UTC+09:00) Yakutsk",
    value: "Yakutsk Standard Time",
    offset: 9,
  },
  {
    label: "(UTC+11:00) Solomon Is., New Caledonia",
    value: "Central Pacific Standard Time",
    offset: 11,
  },
  {
    label: "(UTC+11:00) Vladivostok",
    value: "Vladivostok Standard Time",
    offset: 11,
  },
  {
    label: "(UTC+12:00) Auckland, Wellington",
    value: "New Zealand Standard Time",
    offset: 12,
  },
  {
    label: "(UTC+12:00) Coordinated Universal Time+12",
    value: "UTC+12",
    offset: 12,
  },
  {
    label: "(UTC+12:00) Fiji",
    value: "Fiji Standard Time",
    offset: 12,
  },
  {
    label: "(UTC+12:00) Magadan",
    value: "Magadan Standard Time",
    offset: 12,
  },
  {
    label: "(UTC+12:00) Petropavlovsk-Kamchatsky - Old",
    value: "Kamchatka Standard Time",
    offset: 13,
  },
  {
    label: "(UTC+13:00) Nuku'alofa",
    value: "Tonga Standard Time",
    offset: 13,
  },
  {
    label: "(UTC+13:00) Samoa",
    value: "Samoa Standard Time",
    offset: 13,
  },
];
export const NAVIGATOR_FILTER_CATEGORIES: {
  label: string;
  icon: JSX.Element;
  filters: (
    | NavigatorFilterMeta
    | {
        label: string;
        subFilters: NavigatorFilterMeta[];
      }
  )[];
}[] = [
  {
    label: "Companies",
    icon: <Briefcase weight="fill" />,
    filters: [
      {
        label: "Current company",
        selectionType: "includeExclude",
        type: "CURRENT_COMPANY",
        typeaheadType: "COMPANY_WITH_LIST",
        isFixedTypeahead: false,
      },
      {
        label: "Company headcount",
        selectionType: "multiSelect",
        type: "COMPANY_HEADCOUNT",
        typeaheadType: "COMPANY_SIZE",
        isFixedTypeahead: true,
      },
      {
        label: "Past company",
        selectionType: "includeExclude",
        type: "PAST_COMPANY",
        typeaheadType: "COMPANY_WITH_LIST",
        isFixedTypeahead: false,
      },
      {
        label: "Company type",
        selectionType: "multiSelect",
        type: "COMPANY_TYPE",
        typeaheadType: "COMPANY_TYPE",
        isFixedTypeahead: true,
      },
      {
        label: "Company Headquarters location",
        selectionType: "includeExclude",
        type: "COMPANY_HEADQUARTERS",
        typeaheadType: "BING_GEO",
        isFixedTypeahead: false,
      },
    ],
  },
  {
    label: "Role",
    icon: <UserList weight="fill" />,
    filters: [
      {
        label: "Function",
        selectionType: "includeExclude",
        type: "FUNCTION",
        typeaheadType: "FUNCTION",
        isFixedTypeahead: false,
      },
      {
        label: "Current Job title",
        selectionType: "includeExclude",
        type: "CURRENT_TITLE",
        typeaheadType: "TITLE",
        isFixedTypeahead: false,
      },
      {
        label: "Seniority level",
        selectionType: "includeExclude",
        type: "SENIORITY_LEVEL",
        typeaheadType: "SENIORITY_V2",
        isFixedTypeahead: true,
      },
      {
        label: "Past job title",
        selectionType: "includeExclude",
        type: "PAST_TITLE",
        typeaheadType: "TITLE",
        isFixedTypeahead: false,
      },
      {
        label: "Years in current company",
        selectionType: "multiSelect",
        type: "YEARS_AT_CURRENT_COMPANY",
        typeaheadType: "TENURE",
        isFixedTypeahead: true,
      },
      {
        label: "Years in current position",
        selectionType: "multiSelect",
        type: "YEARS_IN_CURRENT_POSITION",
        typeaheadType: "TENURE",
        isFixedTypeahead: true,
      },
    ],
  },
  {
    label: "Personal",
    icon: <IdentificationBadge weight="fill" />,
    filters: [
      {
        label: "Geography",
        subFilters: [
          {
            label: "Region",
            selectionType: "includeExclude",
            type: "REGION",
            typeaheadType: "BING_GEO",
            isFixedTypeahead: false,
          },
          {
            label: "Postal code",
            selectionType: "singleSelect",
            type: "POSTAL_CODE",
            typeaheadType: "BING_GEO_POSTAL_CODE",
            isFixedTypeahead: false,
          },
        ],
      },
      {
        label: "Industry",
        selectionType: "includeExclude",
        type: "INDUSTRY",
        typeaheadType: "INDUSTRY",
        isFixedTypeahead: false,
      },
      {
        label: "First name",
        selectionType: "customValue",
        type: "FIRST_NAME",
      },
      {
        label: "Last name",
        selectionType: "customValue",
        type: "LAST_NAME",
      },
      {
        label: "Profile language",
        selectionType: "multiSelect",
        type: "PROFILE_LANGUAGE",
        typeaheadType: "PROFILE_LANGUAGE",
        isFixedTypeahead: true,
      },
      {
        label: "Years of experience",
        selectionType: "multiSelect",
        type: "YEARS_OF_EXPERIENCE",
        typeaheadType: "TENURE",
        isFixedTypeahead: false,
      },
      {
        label: "Groups",
        selectionType: "singleSelect",
        type: "GROUP",
        typeaheadType: "GROUP",
        isFixedTypeahead: false,
      },
      {
        label: "School",
        selectionType: "includeExclude",
        type: "SCHOOL",
        typeaheadType: "SCHOOL",
        isFixedTypeahead: false,
      },
    ],
  },
  {
    label: "Buyer intent",
    icon: <ViewProfileFull />,
    filters: [
      {
        label: "Following your company",
        type: "FOLLOWS_YOUR_COMPANY",
        selectionType: "boolean",
        value: { id: "CF", text: "Following your company" },
      },
      {
        label: "Viewed your profile recently",
        type: "VIEWED_YOUR_PROFILE",
        selectionType: "boolean",
        value: { id: "VYP", text: "Viewed your profile recently" },
      },
    ],
  },
  {
    label: "Best path in",
    icon: <ListStar weight="fill" />,
    filters: [
      {
        label: "Connection",
        type: "RELATIONSHIP",
        selectionType: "multiSelect",
        typeaheadType: "RELATIONSHIP",
        isFixedTypeahead: true,
      },
      {
        label: "Connections of",
        type: "CONNECTION_OF",
        selectionType: "multiSelect",
        typeaheadType: "CONNECTION_OF",
        isFixedTypeahead: false,
      },
      {
        label: "Past colleague",
        type: "PAST_COLLEAGUE",
        selectionType: "boolean",
        value: { id: "PCOLL", text: "Past colleague" },
      },
      {
        label: "Shared experiences",
        type: "WITH_SHARED_EXPERIENCES",
        selectionType: "boolean",
        value: { id: "COMM", text: "Shared experiences" },
      },
    ],
  },
  {
    label: "Recent updates",
    icon: <Bell weight="fill" />,
    filters: [
      {
        label: "Changed jobs",
        type: "RECENTLY_CHANGED_JOBS",
        selectionType: "boolean",
        value: { id: "RPC", text: "Changed jobs" },
      },
      {
        label: "Posted on LinkedIn",
        type: "POSTED_ON_LINKEDIN",
        selectionType: "boolean",
        value: { id: "RPOL", text: "Posted on LinkedIn" },
      },
      {
        label: "Mentioned in news",
        type: "IN_THE_NEWS",
        selectionType: "boolean",
        value: { id: "MITN", text: "Mentioned in news" },
      },
    ],
  },
  {
    label: "Workflow",
    icon: <FunnelSimple weight="fill" />,
    filters: [
      {
        label: "Account lists",
        selectionType: "includeExclude",
        type: "ACCOUNT_LIST",
        typeaheadType: "ACCOUNT_LIST",
        isFixedTypeahead: false,
      },
      {
        label: "Lead Lists",
        selectionType: "includeExclude",
        type: "LEAD_LIST",
        typeaheadType: "LEAD_LIST",
        isFixedTypeahead: false,
      },
      {
        label: "People you interact with",
        selectionType: "includeExclude",
        type: "LEAD_INTERACTIONS",
        typeaheadType: "LEAD_INTERACTIONS",
        isFixedTypeahead: true,
      },
      {
        label: "Saved leads and accounts",
        selectionType: "includeExclude",
        type: "SAVED_LEADS_AND_ACCOUNTS",
        typeaheadType: "SAVED_LEADS_AND_ACCOUNTS",
        isFixedTypeahead: true,
      },
    ],
  },
] as const;

export const MANAGE_CAMPAIGNS_ACTIONS = {
  delete: { icon: Trash, label: "Delete campaigns" },
} as const;

type FiltersType<Keys extends keyof CampaignsSearchParams> = {
  [K in Keys]: {
    icon: JSX.Element;
    label: string;
    options: Record<CampaignsSearchParams[K][number], string>;
  };
};

export const CAMPAIGNS_FILTERS: FiltersType<keyof CampaignsSearchParams> = {
  accounts: {
    icon: <UserList weight="fill" />,
    label: "Accounts",
    options: {},
  },
  status: {
    icon: <CircleDashed weight="fill" />,
    label: "Status",
    options: Object.fromEntries(
      typedObjectEntries(CAMPAIGN_STATES).map(([key, { label }]) => [
        key,
        label,
      ]),
    ) as Record<CampaignsSearchParams["status"][number], string>,
  },
  type: {
    icon: <RowsPlusBottom weight="fill" />,
    label: "Type",
    options: {
      inbound: "Inbound",
      outbound: "Outbound",
    },
  },
  targetingSource: {
    icon: <UsersThree weight="fill" />,
    label: "Targeting source",
    options: Object.fromEntries(
      typedObjectEntries(CAMPAIGN_TYPES).map(([key, { label }]) => [
        key,
        label,
      ]),
    ) as Record<CampaignType, string>,
  },
};

export const CAMPAIGNS_TAB_ITEMS: {
  label: string;
  path: string;
  end?: boolean;
  visibleForPermissions?: UserPermissions;
}[] = [
  { label: "Campaigns", path: "", end: true },
  /*{
    label: "Labels",
    path: "labels",
    visibleForPermissions: ["campaigns_full"],
  },*/
  {
    label: "Blacklist",
    path: "blacklist",
    visibleForPermissions: ["campaigns_full"],
  },
];
